import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ReportDisplayOptionsPanel, type ReportDisplayOptions } from './ReportDisplayOptionsPanel';
import { getAuthHeaders } from '../../api/workosAuth';
import { Button } from '../ui/button';
import { Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const PREVIEW_DEBOUNCE_MS = 400;

function optionsEqual(a: ReportDisplayOptions, b: ReportDisplayOptions | null): boolean {
  if (!b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

export type { ReportDisplayOptions };

export interface ReportPreviewWithDisplayOptionsProps {
  /** Base URL for API (e.g. API_BASE_URL) */
  apiBaseUrl: string;
  /** Execution ID */
  executionId: string;
  /** Template ID (e.g. dcma:v1) */
  templateId: string | null;
  displayOptions: ReportDisplayOptions;
  onDisplayOptionsChange: (value: ReportDisplayOptions) => void;
  theme: 'light' | 'dark';
  className?: string;
  /** Whether the project has a client logo uploaded (enables Client Logo checkbox) */
  hasClientLogo?: boolean;
  /** Client logo URL when available (for display only; backend uses base64 in report) */
  clientLogoUrl?: string | null;
  /** Called when user requests download; parent can trigger download with same options */
  onDownload?: () => void;
  /** Called when preview fetch fails so parent can show error (e.g. in header) */
  onError?: (message: string | null) => void;
  /** User's preferred date format (e.g. DD/MM/YYYY) so report dates use current settings */
  preferredDateFormat?: string;
  /** User's timezone (IANA e.g. Europe/London) so generated date/time honours settings */
  preferredTimezone?: string;
  /** Current project ID (for High Float diagnose button) */
  projectId?: string | null;
}

/** Build options payload for backend (deterministic for cache key) */
function buildOptionsPayload(
  displayOptions: ReportDisplayOptions,
  preferredDateFormat?: string,
  templateId?: string | null,
  preferredTimezone?: string
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    display_options: {
      sections: displayOptions.sections,
      header: displayOptions.header ?? {},
    },
  };
  if (preferredDateFormat) {
    payload.preferred_date_format = preferredDateFormat;
  }
  if (preferredTimezone) {
    payload.preferred_timezone = preferredTimezone;
  }
  if (displayOptions.resource_utilization && Object.keys(displayOptions.resource_utilization).length > 0) {
    (payload.display_options as unknown as Record<string, unknown>).resource_utilization = displayOptions.resource_utilization;
  }
  if (displayOptions.period_performance && (displayOptions.period_performance.period_start || displayOptions.period_performance.period_end)) {
    (payload.display_options as unknown as Record<string, unknown>).period_performance = displayOptions.period_performance;
  }
  // High Float report: always send thresholds (default 5/5 so report shows rows)
  if (templateId && templateId.toLowerCase().includes('high-float-low-float')) {
    const highDays = (displayOptions as unknown as Record<string, unknown>).high_float_threshold_days;
    const lowDays = (displayOptions as unknown as Record<string, unknown>).low_float_threshold_days;
    payload.high_float_threshold_days = (highDays != null && highDays !== '') ? Number(highDays) : 5;
    payload.low_float_threshold_days = (lowDays != null && lowDays !== '') ? Number(lowDays) : 5;
  }
  // High Duration Tasks: send duration threshold (days)
  if (templateId && templateId.toLowerCase().includes('high-duration-tasks')) {
    const days = (displayOptions as unknown as Record<string, unknown>).duration_threshold_days;
    if (days != null && days !== '') payload.duration_threshold_days = Number(days);
  }
  return payload;
}

export const ReportPreviewWithDisplayOptions: React.FC<ReportPreviewWithDisplayOptionsProps> = ({
  apiBaseUrl,
  executionId,
  templateId,
  displayOptions,
  onDisplayOptionsChange,
  theme,
  className = '',
  hasClientLogo = false,
  clientLogoUrl = null,
  onDownload,
  onError,
  preferredDateFormat,
  preferredTimezone,
  projectId,
}) => {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appliedOptions, setAppliedOptions] = useState<ReportDisplayOptions | null>(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugResult, setDebugResult] = useState<Record<string, unknown> | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);

  const isHighFloatReport = templateId != null && templateId.toLowerCase().includes('high-float-low-float');

  const runDiagnose = useCallback(async () => {
    if (!projectId || !apiBaseUrl) return;
    setDebugLoading(true);
    setDebugResult(null);
    setDebugOpen(true);
    try {
      const high = (displayOptions as unknown as Record<string, unknown>).high_float_threshold_days ?? 5;
      const low = (displayOptions as unknown as Record<string, unknown>).low_float_threshold_days ?? 5;
      const params = new URLSearchParams({ project_id: projectId });
      if (high !== '' && high != null) params.set('high_float_threshold_days', String(high));
      if (low !== '' && low != null) params.set('low_float_threshold_days', String(low));
      const headers = await getAuthHeaders();
      const res = await fetch(`${apiBaseUrl}/v1/reports/debug/high-float-low-float?${params.toString()}`, {
        credentials: 'include',
        headers,
      });
      const data = await res.json();
      setDebugResult(data);
    } catch (e) {
      setDebugResult({ error: (e as Error).message });
    } finally {
      setDebugLoading(false);
    }
  }, [apiBaseUrl, projectId, displayOptions]);

  useEffect(() => {
    onError?.(error);
  }, [error, onError]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const displayOptionsRef = useRef(displayOptions);
  displayOptionsRef.current = displayOptions;

  const hasPendingChanges = !optionsEqual(displayOptions, appliedOptions);

  const fetchPdf = useCallback(async (opts: ReportDisplayOptions) => {
    const options = buildOptionsPayload(opts, preferredDateFormat, templateId, preferredTimezone);
    const params = new URLSearchParams();
    if (templateId) params.set('template_id', templateId);
    params.set('options', JSON.stringify(options));
    const url = `${apiBaseUrl}/v1/reports/executions/${executionId}/preview?${params.toString()}`;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setFetching(true);
    setError(null);

    const headers = await getAuthHeaders();
    fetch(url, { credentials: 'include', headers, signal: abortRef.current.signal })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText || 'Preview failed');
        return res.blob();
      })
      .then((blob) => {
        if (blob.type !== 'application/pdf') throw new Error('Expected PDF');
        if (blobUrlRef.current) globalThis.URL.revokeObjectURL(blobUrlRef.current);
        const next = globalThis.URL.createObjectURL(blob);
        blobUrlRef.current = next;
        setPdfBlobUrl(next);
        setAppliedOptions(opts);
      })
      .catch((e: unknown) => {
        if ((e as { name?: string }).name === 'AbortError') return;
        setError((e as Error).message || 'Failed to load preview');
        setPdfBlobUrl(null);
      })
      .finally(() => {
        setFetching(false);
        abortRef.current = null;
      });
  }, [apiBaseUrl, executionId, templateId, preferredDateFormat, preferredTimezone]);

  useEffect(() => {
    if (!executionId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      fetchPdf(displayOptionsRef.current);
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [executionId, templateId, displayOptions, fetchPdf]);

  const applyOptions = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    fetchPdf(displayOptionsRef.current);
  }, [fetchPdf]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        globalThis.URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  return (
    <div className={`flex h-full w-full gap-4 ${className}`}>
      <div className="flex-none w-[320px] flex flex-col gap-3 rounded-lg border border-border bg-card overflow-y-auto px-4 py-4">
        <ReportDisplayOptionsPanel
          templateId={templateId ?? ''}
          displayOptions={displayOptions}
          onDisplayOptionsChange={onDisplayOptionsChange}
          hasClientLogo={hasClientLogo}
          showApplyButton
          onApply={applyOptions}
          hasPendingChanges={hasPendingChanges}
          applying={fetching}
        />
        {onDownload && (
          <Button
            type="button"
            variant="default"
            size="sm"
            className="w-full"
            onClick={onDownload}
          >
            <Download size={14} className="mr-2" />
            Download PDF
          </Button>
        )}
        {isHighFloatReport && projectId && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={runDiagnose}
            disabled={debugLoading}
          >
            {debugLoading ? 'Running diagnosis…' : 'Diagnose report (why 0 rows?)'}
          </Button>
        )}
      </div>

      <div className="flex-1 min-w-0 h-full overflow-auto flex flex-col rounded-lg border border-border bg-card">
        {fetching && !pdfBlobUrl ? (
          <div className="flex items-center justify-center flex-1 text-muted-foreground text-sm">
            Loading preview…
          </div>
        ) : error ? (
          <div className="flex items-center justify-center flex-1 text-sm text-destructive">
            {error}
          </div>
        ) : pdfBlobUrl ? (
          <iframe
            key={pdfBlobUrl}
            src={pdfBlobUrl}
            title="Report PDF Preview"
            className="flex-1 w-full min-h-0 border-0 bg-muted rounded-b-lg"
          />
        ) : (
          <div className="flex items-center justify-center flex-1 text-sm text-muted-foreground">
            No preview
          </div>
        )}
      </div>

      <Dialog open={debugOpen} onOpenChange={setDebugOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>High Float report diagnosis</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex-1 min-h-0 rounded border bg-muted/50 p-4">
            {debugLoading && !debugResult && <p className="text-sm text-muted-foreground">Loading…</p>}
            {debugResult != null && (
              <pre className="text-xs whitespace-pre-wrap break-words font-mono">
                {JSON.stringify(debugResult, null, 2)}
              </pre>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
